import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { getInvestorApiUrl } from "../../utils/config";
import { ApiRequestParams, handleFetchApi } from "../../utils/api";

// Define types for state and response data
interface State {
  value: string;
  label: string;
  id: string;
}

interface County {
  id: string;
  groupBy: string;
  label: string;
  value: string;
}

interface StateCountyState {
  states: State[];
  counties: County[];
  loading: boolean;
  error: string | null;
  selectedStateId: string | null;
}

const initialState: StateCountyState = {
  states: [],
  counties: [],
  loading: false,
  error: null,
  selectedStateId: null,
};

// Async thunk to fetch states
export const fetchStates = createAsyncThunk<State[], void>(
  "stateCounty/fetchStates",
  async (_, thunkApi) => {
    try {
      const params: ApiRequestParams = {
        baseUrl: getInvestorApiUrl(),
        endpoint: "states"
      }
      const response = await handleFetchApi(params)
      // const baseUrl = getInvestorApiUrl();
      // const response = await axios.get(`${baseUrl}/states`);
      const data = response.data.data.map((state: any) => ({
        value: state.stateCode,
        label: state.stateName,
        id: state.id,
      }));
      return data;
    } catch (error: any) {
      return thunkApi.rejectWithValue(
        error.response?.data || "Failed to fetch states"
      );
    }
  }
);

// Async thunk to fetch counties by state ID
export const fetchCountiesByState = createAsyncThunk<County[], string[] | string>(
  "stateCounty/fetchCountiesByState",
  async (stateIds, thunkApi) => {
    try {
      const queryString = Array.isArray(stateIds)
        ? stateIds.map((id) => `stateIds=${id}`).join("&")
        : `stateIds=${stateIds}`;

      const params: ApiRequestParams = {
        baseUrl: getInvestorApiUrl(),
        endpoint: `counties?${queryString}`
      }
      const response = await handleFetchApi(params);
     
      // const response = await axios.get(`${baseUrl}/counties?${queryString}`);
      const countiesData = response.data.data.flatMap((item: any) => item.counties);

      return countiesData.map((county: any) => ({
        id: county.id,
        groupBy: county.stateName,
        label: county.countyName,
        value: county.countyName,
      }));
    } catch (error: any) {
      return thunkApi.rejectWithValue(
        error.response?.data || "Failed to fetch counties"
      );
    }
  }
);

const countyStateSlice = createSlice({
  name: "stateCounty",
  initialState,
  reducers: {
    resetState: (state) => {
      state.states = [];
      state.counties = [];
      state.error = null;
      state.selectedStateId = null;
    },
    setSelectedStateId: (state, action: PayloadAction<string | null>) => {
      state.selectedStateId = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchStates.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchStates.fulfilled, (state, action: PayloadAction<State[]>) => {
        state.loading = false;
        state.states = action.payload;
      })
      .addCase(fetchStates.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string || "Failed to fetch states";
      })
      .addCase(fetchCountiesByState.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCountiesByState.fulfilled, (state, action: PayloadAction<County[]>) => {
        state.loading = false;
        state.counties = action.payload;
      })
      .addCase(fetchCountiesByState.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string || "Failed to fetch counties";
      });
  },
});

export const { resetState, setSelectedStateId } = countyStateSlice.actions;
export default countyStateSlice.reducer;
